<script lang="ts">
  // The introduction dialog: illustration column (OnboardingArt) on the left,
  // progress, the current step and the navigation on the right. Phones get one
  // column with a smaller illustration inside the step. Escape = "later";
  // a backdrop click does nothing.
  import { tick, untrack, type Component } from 'svelte';
  import { fade, fly } from 'svelte/transition';
  import { ArrowLeft, ArrowRight, X } from '@lucide/svelte';
  import { t } from '../i18n.svelte';
  import { initials } from '../format';
  import { Button, Modal, Stepper } from '../ui';
  import OnboardingArt from './OnboardingArt.svelte';
  import Illustration from './Illustration.svelte';
  import WelcomeStep from './steps/WelcomeStep.svelte';
  import WorkStep from './steps/WorkStep.svelte';
  import WeekStep from './steps/WeekStep.svelte';
  import PeriodStep from './steps/PeriodStep.svelte';
  import RoundStep from './steps/RoundStep.svelte';
  import ClientStep from './steps/ClientStep.svelte';
  import NotifStep from './steps/NotifStep.svelte';
  import DoneStep from './steps/DoneStep.svelte';
  import { flow, STEPS, type StepKey } from './state.svelte';

  const VIEWS: Record<StepKey, Component> = {
    welcome: WelcomeStep,
    work: WorkStep,
    week: WeekStep,
    period: PeriodStep,
    round: RoundStep,
    client: ClientStep,
    notif: NotifStep,
    done: DoneStep
  };
  const View = $derived(VIEWS[flow.step]);

  let root = $state<HTMLElement | null>(null);
  let stage = $state<HTMLElement | null>(null);

  $effect(() => {
    const el = root;
    if (!el) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) {
        event.preventDefault();
        flow.finish('later');
      }
    };
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  });

  // After a step change: focus the new step (heading or first field) once it is in place.
  $effect(() => {
    if (!flow.moves) return;
    untrack(async () => {
      await tick();
      setTimeout(() => {
        stage?.querySelector<HTMLElement>('.step:last-child [data-step-focus]')?.focus({ preventScroll: true });
        stage?.scrollTo({ top: 0 });
      }, flow.still ? 0 : 120);
    });
  });
</script>

<Modal open={flow.open} variant="bare" size="xl" dismissible={false} ariaLabel={t('onb.dialog')}>
  <div class="shell" bind:this={root}>
    <OnboardingArt />

    <form class="content" onsubmit={flow.next} novalidate>
      <header class="top">
        <Stepper total={STEPS.length} current={flow.index} partial={false} />
        <div class="top-row">
          <span class="count">{t('onb.step_of', { step: flow.index + 1, total: STEPS.length })}</span>
          {#if flow.step !== 'done'}
            <Button variant="ghost" size="sm" title={t('onb.later_hint')} onclick={() => flow.finish('later')}>
              {t('onb.later')}
              {#snippet iconRight()}<X />{/snippet}
            </Button>
          {/if}
        </div>
      </header>

      <div class="stage" bind:this={stage}>
        {#key flow.index}
          <div class="step" in:fly={flow.stepIn} out:fade={flow.stepOut}>
            <div class="step-art" aria-hidden="true">
              <Illustration kind={flow.step} size={190} color={flow.clientColor} initials={flow.clientName ? initials(flow.clientName) : ''} />
            </div>
            <View />
          </div>
        {/key}
      </div>

      {#if flow.step !== 'done'}
        <footer class="foot">
          {#if flow.index > 0}
            <Button variant="ghost" disabled={flow.busy} onclick={() => flow.go(flow.index - 1)}>
              {#snippet icon()}<ArrowLeft />{/snippet}
              {t('onb.back')}
            </Button>
          {/if}
          <span class="spacer"></span>
          {#if flow.index > 0}
            <Button variant="ghost" disabled={flow.busy} onclick={() => flow.go(flow.index + 1)}>{t('onb.skip')}</Button>
          {/if}
          <Button type="submit" variant="primary" loading={flow.busy} disabled={flow.busy} data-autofocus>
            {#snippet iconRight()}<ArrowRight />{/snippet}
            {flow.index === 0 ? t('onb.start') : flow.busy ? t('onb.saving') : t('onb.next')}
          </Button>
        </footer>
      {/if}
    </form>
  </div>
</Modal>

<style>
  .shell {
    display: grid;
    grid-template-columns: minmax(300px, 40%) minmax(0, 1fr);
    height: min(680px, calc(100dvh - 2 * var(--space-6)));
  }

  .content { display: flex; flex-direction: column; min-width: 0; min-height: 0; background: var(--surface); }
  .top { display: flex; flex-direction: column; gap: var(--space-3); padding: var(--space-5) var(--space-8) 0; }
  .top-row { display: flex; align-items: center; justify-content: space-between; min-height: var(--control-h-sm); }
  .count { font-size: var(--text-xs); font-weight: var(--weight-semibold); color: var(--text-3); }

  .stage { flex: 1; display: grid; min-height: 0; padding: var(--space-2) var(--space-8) var(--space-6); overflow: hidden auto; }
  .step { grid-area: 1 / 1; display: flex; flex-direction: column; gap: var(--space-1_5); min-width: 0; }
  .step-art { display: none; }

  .foot { display: flex; align-items: center; gap: var(--space-2); padding: var(--space-3) var(--space-8); border-top: 1px solid var(--border); }

  @media (max-width: 900px) {
    .shell { grid-template-columns: minmax(240px, 34%) minmax(0, 1fr); }
  }
  /* Phone: one column, the illustration inside the step. */
  @media (max-width: 720px) {
    .shell { grid-template-columns: minmax(0, 1fr); padding-top: env(safe-area-inset-top); }
    .top { padding: var(--space-3) var(--space-5) 0; }
    .stage { padding: var(--space-1) var(--space-5) var(--space-5); }
    .step-art {
      display: grid;
      place-items: center;
      margin: var(--space-1) calc(var(--space-5) * -1) var(--space-2);
      padding: var(--space-2_5) 0 var(--space-1);
      background: radial-gradient(60% 80% at 50% 40%, var(--brand-softer), transparent 70%);
    }
    .foot { padding: var(--space-3) var(--space-4) calc(var(--space-3) + env(safe-area-inset-bottom)); }
  }
  @media (max-width: 640px) {
    .shell { height: calc(100dvh - 2 * var(--space-3)); }
  }
</style>
